import { Box, Grid, useTheme } from '@mui/material';
import { H5, Small } from 'components/Typography';
import { MessageCircle, Star, ThumbsUp } from 'lucide-react';
import { useState } from 'react';

const CommunityPostCard = ({ item, isMobile, onClick, ...props }) => {
  const theme = useTheme();
  const [isStarred, setIsStarred] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [starCount, setStarCount] = useState(item.starCount || 0);
  const [likeCount, setLikeCount] = useState(item.likeCount || 0);

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleStarClick = e => {
    e.stopPropagation();
    setIsStarred(!isStarred);
    setStarCount(prev => (isStarred ? prev - 1 : prev + 1));
  };

  const handleLikeClick = e => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    setLikeCount(prev => (isLiked ? prev - 1 : prev + 1));
  };

  return (
    <Grid onClick={handleClick} item xs={12} key={item.id} {...props}>
      <Box
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': isMobile
            ? {}
            : {
                transform: 'translateY(-2px)',
              },
        }}
      >
        {isMobile ? (
          // Mobile Layout
          <>
            {/* Row 1: Category Tag + Title + Stats (Right aligned) */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={0.5}
            >
              {/* Left: Category + Title */}
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.mainBlue,
                    color: theme.palette.primary.white,
                    padding: '2px 8px',
                    borderRadius: '100px',
                    fontSize: '10px',
                    fontWeight: '600',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                >
                  {item.category}
                </Box>
                <H5
                  sx={{
                    fontWeight: 'bold',
                    lineHeight: 1.3,
                    fontSize: '16px',
                    color: theme.palette.text.primary,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {item.title}
                </H5>
              </Box>

              {/* Right: Engagement Metrics */}
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ flexShrink: 0 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleStarClick}
                >
                  <Star
                    size={14}
                    color={
                      isStarred
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={isStarred ? theme.palette.primary.mainBlue : 'none'}
                  />
                  <Small
                    sx={{
                      color: isStarred
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt,
                      fontSize: '12px',
                      fontWeight: isStarred ? '600' : 'normal',
                    }}
                  >
                    {starCount}
                  </Small>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleLikeClick}
                >
                  <ThumbsUp
                    size={14}
                    color={
                      isLiked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={isLiked ? theme.palette.primary.mainBlue : 'none'}
                  />
                  <Small
                    sx={{
                      color: isLiked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt,
                      fontSize: '12px',
                      fontWeight: isLiked ? '600' : 'normal',
                    }}
                  >
                    {likeCount}
                  </Small>
                </Box>

                <Box display="flex" alignItems="center" gap={0.5}>
                  <MessageCircle
                    size={14}
                    color={theme.palette.primary.greytxt}
                    fill="none"
                  />
                  <Small
                    sx={{
                      color: theme.palette.primary.greytxt,
                      fontSize: '12px',
                    }}
                  >
                    {item.commentCount}
                  </Small>
                </Box>
              </Box>
            </Box>

            {/* Row 2: Content with Thumbnail */}
            <Box
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              mb={0}
              flex={1}
            >
              <Box
                sx={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: '#f5f5f5',
                }}
              >
                <img
                  src={
                    item.thumbnail || '/static/community/default-thumbnail.png'
                  }
                  alt="thumbnail"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <Box flex={1}>
                <Small
                  sx={{
                    color: theme.palette.primary.greytxt,
                    lineHeight: 1.4,
                    fontSize: '13px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.content}
                </Small>
              </Box>
            </Box>
          </>
        ) : (
          // Desktop Layout (Original)
          <>
            {/* Category Tag, Title, and Icons on same row */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.mainBlue,
                  color: theme.palette.primary.white,
                  padding: '3px 10px',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              >
                {item.category}
              </Box>
              <H5
                sx={{
                  fontWeight: 'bold',
                  lineHeight: 1.3,
                  fontSize: '15px',
                  color: theme.palette.text.primary,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {item.title}
              </H5>

              {/* Engagement Metrics */}
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{ flexShrink: 0 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleStarClick}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <Star
                    size={16}
                    color={
                      isStarred
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={isStarred ? theme.palette.primary.mainBlue : 'none'}
                  />
                  <Small
                    sx={{
                      color: isStarred
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt,
                      fontSize: '13px',
                      fontWeight: isStarred ? '600' : 'normal',
                    }}
                  >
                    {starCount}
                  </Small>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleLikeClick}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <ThumbsUp
                    size={16}
                    color={
                      isLiked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={isLiked ? theme.palette.primary.mainBlue : 'none'}
                  />
                  <Small
                    sx={{
                      color: isLiked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt,
                      fontSize: '13px',
                      fontWeight: isLiked ? '600' : 'normal',
                    }}
                  >
                    {likeCount}
                  </Small>
                </Box>

                <Box display="flex" alignItems="center" gap={0.5}>
                  <MessageCircle
                    size={16}
                    color={theme.palette.primary.greytxt}
                    fill="none"
                  />
                  <Small
                    sx={{
                      color: theme.palette.primary.greytxt,
                      fontSize: '13px',
                    }}
                  >
                    {item.commentCount}
                  </Small>
                </Box>
              </Box>
            </Box>

            {/* Content with Thumbnail */}
            <Box
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              mb={0}
              flex={1}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: '#f5f5f5',
                }}
              >
                <img
                  src={
                    item.thumbnail || '/static/community/default-thumbnail.png'
                  }
                  alt="thumbnail"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>

              {/* Content Text */}
              <Box flex={1}>
                <Small
                  sx={{
                    color: theme.palette.primary.greytxt,
                    lineHeight: 1.4,
                    fontSize: '14px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.content}
                </Small>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Grid>
  );
};

export default CommunityPostCard;
